import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Trash2, ChevronDown, ChevronRight, Settings2, GripVertical, Maximize2, CornerDownRight } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Cell, ItemName } from "./BoardCells";
import { GROUP_COLORS } from "./boardConstants";

export function GroupMenu({ group, onColor, onDelete, canDelete }) {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`board-group-menu-${group.id}`} className="text-text-secondary hover:text-text-primary opacity-0 group-hover/grp:opacity-100 transition-opacity">
        <Settings2 className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 left-0 w-[180px] rounded-lg border bg-surface shadow-xl p-2" data-testid={`board-group-menu-panel-${group.id}`}>
          <div className="text-[10px] uppercase tracking-wide text-text-secondary mb-1.5 px-1">{t("board_group_color")}</div>
          <div className="flex flex-wrap gap-1.5 mb-2 px-1">
            {GROUP_COLORS.map((c) => (
              <button key={c} onClick={() => { onColor(c); setOpen(false); }} data-testid={`board-group-color-${group.id}-${c.replace("#", "")}`}
                className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110" style={{ backgroundColor: c, borderColor: group.color === c ? "var(--text-primary)" : "transparent" }} />
            ))}
          </div>
          {canDelete && (
            <button onClick={() => { onDelete(); setOpen(false); }} data-testid={`board-group-delete-${group.id}`}
              className="w-full flex items-center gap-1.5 px-1.5 py-1.5 rounded-md text-xs text-red-400 hover:bg-red-500/15 border-t pt-2">
              <Trash2 className="w-3.5 h-3.5" /> {t("board_delete_group")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Inline "+ Add subitem" input (Enter adds & keeps focus to add another).
export function AddSubitemRow({ onAdd, colCount }) {
  const { t } = useApp();
  const [val, setVal] = useState("");
  const submit = async () => { const v = val.trim(); if (!v) return; setVal(""); await onAdd(v); };
  return (
    <tr>
      <td colSpan={colCount} className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          <CornerDownRight className="w-3.5 h-3.5 text-text-secondary shrink-0" />
          <input value={val} onChange={(e) => setVal(e.target.value)} placeholder={t("board_add_subitem")}
            data-testid="board-add-subitem-input"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            className="flex-1 bg-transparent text-sm outline-none" />
          {val.trim() && <span className="text-[10px] text-text-secondary">{t("board_subitem_hint")}</span>}
        </div>
      </td>
    </tr>
  );
}

// Draggable, sortable top-level item row. Nesting is done via the subitem zone (see SubZone).
export function SortableItemRow({ item, group, columns, subitems, expanded, focusId, promoteTarget,
  onSetCell, onSetName, onDelete, onToggleExpand, onOpen, onEditColumn, onClearFocus, renderSubitems }) {
  const { t } = useApp();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <React.Fragment>
      <tr ref={setNodeRef} style={style} className={cn("border-b last:border-0 transition-colors group/row", promoteTarget ? "bg-primary/5" : "hover:bg-surface-hover")} data-testid={`board-item-${item.id}`}>
        <td className="px-2 py-1.5 sticky left-0 bg-background group-hover/row:bg-surface-hover transition-colors" style={{ borderLeft: `3px solid ${group.color}` }}>
          <div className="flex items-center gap-1">
            <button {...attributes} {...listeners} data-testid={`board-item-drag-${item.id}`}
              className="shrink-0 cursor-grab active:cursor-grabbing touch-none flex items-center justify-center w-4 h-6 group/handle" title={t("board_drag_hint")}>
              <span className="block w-2.5 h-2.5 rounded-[3px] opacity-60 group-hover/row:opacity-100 group-hover/handle:scale-110 transition-all" style={{ backgroundColor: group.color }} />
            </button>
            <button onClick={() => onToggleExpand(item.id)} data-testid={`board-item-expand-${item.id}`} className="shrink-0 text-text-secondary hover:text-text-primary inline-flex items-center" title={t("board_subitems")}>
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <ItemName value={item.name} onSave={(v) => onSetName(item, v)} testid={`board-item-name-${item.id}`} startEditing={item.id === focusId} onStartConsumed={onClearFocus} maxWidth="48vw" />
            {subitems.length > 0 && <span className="shrink-0 text-[10px] text-text-secondary px-1.5 py-0.5 rounded-full bg-surface-hover" data-testid={`board-item-subcount-${item.id}`}>{subitems.length}</span>}
            <button onClick={() => onOpen(item.id)} data-testid={`board-item-open-${item.id}`} className="opacity-0 group-hover/row:opacity-100 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px] text-text-secondary hover:text-text-primary transition-all shrink-0">
              <Maximize2 className="w-3 h-3" /> {t("board_open")}
            </button>
          </div>
        </td>
        {columns.map((c) => (
          <td key={c.id} className="px-2 py-1.5"><Cell col={c} value={(item.values || {})[c.id]} onChange={(v) => onSetCell(item, c.id, v)} testid={`cell-${item.id}-${c.id}`} onEditColumn={(col) => onEditColumn(col, "main")} /></td>
        ))}
        <td className="px-2 py-1.5 text-center">
          <button onClick={() => onDelete(item)} data-testid={`board-item-delete-${item.id}`} className="opacity-0 group-hover/row:opacity-100 text-text-secondary hover:text-red-400 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
        </td>
      </tr>
      {expanded && renderSubitems(item)}
    </React.Fragment>
  );
}

// Draggable, sortable subitem row (reorder among siblings, or drag onto a top-level row to promote).
export function SortableSubitemRow({ sub, subCols, color, focusId, colWidth, onSetCell, onSetName, onDelete, onEditColumn, onClearFocus }) {
  const { t } = useApp();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sub.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <tr ref={setNodeRef} style={style} className="border-b last:border-0 hover:bg-surface-hover transition-colors group/srow" data-testid={`subitem-${sub.id}`}>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <button {...attributes} {...listeners} data-testid={`subitem-drag-${sub.id}`} className="shrink-0 cursor-grab active:cursor-grabbing touch-none flex items-center justify-center w-3.5 h-5" title={t("board_drag_hint")}>
            <span className="block w-2 h-2 rounded-[2px] opacity-50 group-hover/srow:opacity-90 transition-all" style={{ backgroundColor: color }} />
          </button>
          <CornerDownRight className="w-3 h-3 text-text-secondary/50 shrink-0" />
          <ItemName value={sub.name} onSave={(v) => onSetName(sub, v)} testid={`subitem-name-${sub.id}`} startEditing={sub.id === focusId} onStartConsumed={onClearFocus} maxWidth="40vw" />
          <button onClick={() => onDelete(sub)} data-testid={`subitem-delete-${sub.id}`} className="opacity-0 group-hover/srow:opacity-100 text-text-secondary hover:text-red-400 transition-all shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </td>
      {subCols.map((c) => (
        <td key={c.id} className="px-2 py-1.5"><Cell col={c} value={(sub.values || {})[c.id]} onChange={(v) => onSetCell(sub, c.id, v)} testid={`subcell-${sub.id}-${c.id}`} onEditColumn={(col) => onEditColumn(col, "sub")} /></td>
      ))}
      <td />
    </tr>
  );
}

// Subitem area = a drop zone. While a nestable top-level item is dragged, dropping here nests it.
export function SubZone({ parentId, colCount, bg, highlight, active, dragName, children }) {
  const { t } = useApp();
  const { setNodeRef, isOver } = useDroppable({ id: `subzone-${parentId}` });
  const hot = highlight && (isOver || active);
  return (
    <tr data-testid={`subitems-${parentId}`}>
      <td colSpan={colCount} className="p-0" style={{ background: bg }}>
        <div ref={setNodeRef} className={cn("pl-9 pr-3 py-2 transition-all rounded-xl", hot && "ring-2 ring-inset ring-primary/70 bg-primary/5")} data-testid={`subzone-drop-${parentId}`}>
          {children}
          {hot && (
            <div className="mt-1.5 h-9 rounded-md border-2 border-dashed border-primary/70 bg-primary/5 flex items-center gap-2 px-3 text-xs font-medium text-primary" data-testid={`subzone-hint-${parentId}`}>
              <CornerDownRight className="w-3.5 h-3.5" />
              <span className="truncate">{dragName ? `${t("board_nest_here")}: ${dragName}` : t("board_nest_here")}</span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
